import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ColourEnergyIndicator,
  TextV2,
} from '@insights-ltd/design-library/components';
import { Link } from 'react-router';
import Button from '@mui/material/Button';
import { Box, styled } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import LinkEmail from 'components/LinkEmail/LinkEmail';
import { LatestProfileText } from 'utils/LatestProfileText';
import downloadProfile from 'api/downloadProfile';
import DownloadProgressDialog from 'components/DownloadProgressDialog/DownloadProgressDialog';
import { useRequestErrorContext } from 'components/RequestErrorDialog/RequestErrorProvider';
import { Dialects } from 'types/dialects';
import { getSupportedDownloadDialect } from 'variables/dialects';
import { DataTable } from 'components/DataTable/DataTableV2';
import { TransformedLearnerAndInviteeResponse } from 'pages/Learners/LearnersTab/transformLearners';
import { Paginator } from 'components/hooks/usePaginationV2';
import { ApiPaginationProps } from 'components/Pagination/Pagination';

const StyledDiv = styled('div')({});

const MaxWidthText = styled(TextV2)({
  maxWidth: '266px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

type LearnersListProps = {
  paginator: Paginator<TransformedLearnerAndInviteeResponse>;
  apiPaginationProps?: ApiPaginationProps;
};

type LearnersDataStructure = Record<
  keyof Omit<
    TransformedLearnerAndInviteeResponse,
    | 'id'
    | 'learnerId'
    | 'knownAs'
    | 'leadingColourEnergy'
    | 'latestProfileEvaluationAt'
    | 'latestEvaluation'
    | 'preferredDialect'
  >,
  string
>;

const StyledListItemWrapper = styled('div')(({ theme }) => ({
  '> * + *': {
    marginTop: theme.spacing(spacingSizeMap.XS),
  },
}));

const LearnersList = ({ paginator, apiPaginationProps }: LearnersListProps) => {
  const { t } = useTranslation();

  const { openErrorModal } = useRequestErrorContext();

  const [showDownloadProfileDialog, setShowDownloadProfileDialog] =
    useState(false);

  const dataStructure: LearnersDataStructure = {
    fullName: t('ui.event-management.learners.heading.full-name'),
    colourScores: t('ui.event-management.learners.heading.colour-energies'),
    primaryEmail: t('ui.event-management.learners.heading.email'),
    latestProfile: t('ui.event-management.learners.heading.latest-profile'),
    organisation: t('ui.event-management.learners.heading.organisation'),
  };

  const onDownloadProfile = async (
    learnerId: string,
    profileId: string,
    dialect: Dialects,
  ) => {
    try {
      setShowDownloadProfileDialog(true);
      await downloadProfile(learnerId, profileId, dialect, 'NAMED');
    } catch {
      setShowDownloadProfileDialog(false);
      openErrorModal();
    } finally {
      setShowDownloadProfileDialog(false);
    }
  };

  const columnPredicate = (
    columnName: keyof TransformedLearnerAndInviteeResponse,
    columnValue: string | number,
    props: Record<string, any>,
  ) => {
    const { latestProfile, latestEvaluation } = props;
    switch (columnName) {
      case 'latestProfile':
        return (
          <LatestProfileText
            latestProfile={latestProfile}
            latestEvaluation={latestEvaluation}
            onClick={() => {
              onDownloadProfile(
                props.learnerId,
                latestProfile?.id ?? '',
                getSupportedDownloadDialect(
                  latestProfile?.profileType,
                  props.preferredDialect,
                ),
              );
            }}
          />
        );
      case 'primaryEmail':
        return (
          <LinkEmail
            sx={(theme) => ({
              fontWeight: `${theme.typography.fontWeightMedium} !important`,
            })}
            email={columnValue as string}
          />
        );
      case 'fullName':
        return (
          <Box
            sx={[
              {
                display: 'flex',
              },
              (theme) => ({
                '> a': {
                  display: 'flex',
                  justifyContent: 'flex-start',
                  color: theme.palette.primary.main,
                  fontWeight: theme.typography.fontWeightBold,
                  textDecoration: 'none',
                  textDecorationColor: theme.palette.primary.main,
                  paddingLeft: '0',
                  '&:visited': {
                    color: theme.palette.primary.main,
                    textDecorationColor: theme.palette.primary.main,
                  },
                },
              }),
            ]}
          >
            <Button
              component={Link}
              to={`/learners/${props.organisation.id}/${props.learnerId}`}
              sx={(theme) => ({
                '&:hover': {
                  backgroundColor: theme.palette.grey[300],
                },
              })}
            >
              <MaxWidthText component="span" color="primary">
                {columnValue}
              </MaxWidthText>
            </Button>
          </Box>
        );
      case 'organisation':
        return (
          <Button
            component={Link}
            to={`/organisations/${props.organisation.id}`}
            sx={(theme) => ({
              '&:hover': {
                backgroundColor: theme.palette.grey[300],
              },
              padding: '0',
              minWidth: `0 !important`,
            })}
          >
            <MaxWidthText component="span" variant="bodyBold" color="primary">
              {props.organisation.name}
            </MaxWidthText>
          </Button>
        );
      case 'colourScores': {
        const scores = props.colourScores?.conscious ?? {};
        return (
          <StyledDiv data-testid="colour-scores" sx={{ maxWidth: '330px' }}>
            <ColourEnergyIndicator {...scores} />
          </StyledDiv>
        );
      }

      default:
        return <TextV2>{columnValue}</TextV2>;
    }
  };

  return (
    <StyledListItemWrapper>
      <DataTable
        dataStructure={dataStructure}
        columnPredicate={columnPredicate}
        apiPaginationProps={apiPaginationProps}
        paginator={paginator}
      />
      <DownloadProgressDialog
        open={showDownloadProfileDialog}
        onClose={() => setShowDownloadProfileDialog(false)}
      />
    </StyledListItemWrapper>
  );
};
export default LearnersList;
